import {
  CAPACITY_SUGGESTIONS,
  displayName,
  emailWarning,
  phoneWarning,
  type AppointmentContact,
  type AssignedInquiry,
  type UpdateInquiryContacts,
} from '@ghp/domain'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  DataField,
  Field,
  FieldValue,
  InfoNote,
  Input,
  SectionLabel,
} from '@ghp/ui'
import { Pencil, PhoneCall, TriangleAlert, UserRound, WifiOff } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import claim from './claimed.module.css'
import s from './ContactsCard.module.css'

type Owner = NonNullable<AssignedInquiry['owner']>

/**
 * La correction des coordonnées **en ligne** — l'écriture directe que le serveur accepte depuis la
 * tablette (`PATCH /inquiries/:id/contacts`, seconde exception à D4).
 */
export interface ContactsEditing {
  /**
   * `null` quand la correction est possible ; sinon **la raison**, affichée à côté du bouton
   * désactivé. Un bouton grisé sans explication se lit comme une panne (M21).
   */
  unavailable: string | null
  /** Rejette avec le message à montrer mot pour mot quand le serveur refuse. */
  save: (request: UpdateInquiryContacts) => Promise<void>
}

export interface ContactsCardProps {
  contact: AppointmentContact | null
  owner: Owner | null
  /** Fiche transmise : il n'y a plus rien à corriger d'ici, la demande est au bureau. */
  readOnly?: boolean
  /** Absent : les coordonnées se lisent, le geste n'est pas offert. */
  editing?: ContactsEditing
  /**
   * La correction en cours, **tenue par l'éditeur** : les onglets démontent leur contenu, et une
   * correction gardée dans la carte se perdait au premier passage sur « Anomalies ». Absent, la
   * carte la tient elle-même.
   */
  draft?: ContactsDraft | null
  onDraftChange?: (draft: ContactsDraft | null) => void
  /** La case « Une autre personne est sur place » (#555) — cochée, le volet du signataire s'ouvre. */
  otherPersonPresent?: boolean
  /** Absent, ou sans contact sur la demande, le volet ne se monte pas : il n'y a rien à fusionner. */
  onOtherPersonPresentChange?: (present: boolean) => void
  /** Les champs du signataire (`AttendeeFields`), que le volet ouvre sous sa case. */
  attendee?: ReactNode
  /**
   * La qualité constatée du contact qui signe (`draft.signerCapacity`). Avec son rappel, elle se
   * saisit à la place de la qualité annoncée tant que personne d'autre n'est sur place (M20).
   */
  signerCapacity?: string
  onSignerCapacityChange?: (capacity: string) => void
}

/**
 * Une correction en cours : ce que l'agent tape, et **ce qu'il avait sous les yeux en commençant**.
 * Comparer à la base figée, pas aux données reçues depuis : si la tournée redescend pendant la
 * saisie, un champ non touché repartirait sinon avec une valeur que le bureau vient de corriger.
 */
export interface ContactsDraft {
  baseline: Values
  values: Values
}

/**
 * La carte « **Contact** » de l'onglet Infos — **qui appeler et qui rencontrer** (le contact du
 * rendez-vous), et le bouton « Modifier » du prototype, qui corrige aussi le propriétaire.
 *
 * ⚠️ **Un seul bloc par défaut** (#555, recette du 20/10/2026 : « Fusionner les deux cases ») : le
 * contact est aussi celui qui signe. Quand quelqu'un d'autre ouvre la porte — une agence a pris
 * l'appel, le locataire est là —, le volet « Une autre personne est sur place » s'ouvre sous le
 * contact, dans la même carte : la case est son titre (choix du développeur, 22/10/2026).
 *
 * ⚠️ **Le propriétaire se lit dans sa propre carte** depuis #350 (`OwnerCard`, frame Terrain
 * 1.1b), et se corrige ici : la correction est une seule écriture en ligne pour les deux
 * personnes, et deux boutons « Modifier » en tiendraient deux en parallèle.
 *
 * ⚠️ **Au prototype, « Modifier » ne fait qu'un toast** (`mocks/ghp-ct/screens.js`, onglet Infos).
 * Le développeur l'a voulu réel (23/09) : l'agent a la personne devant lui, et faire attendre la
 * validation du bureau pour un numéro de téléphone laisserait la demande avec un contact faux
 * pendant des jours. D'où **une écriture directe, en ligne seulement** — jamais par l'outbox : un
 * échec se dit à l'agent au lieu de se rejouer plus tard sur une demande qui aura changé de main.
 *
 * ⚠️ **La personne rencontrée n'est pas ici** : elle appartient au contrôle, voyage dans la
 * proposition et se saisit hors ligne (carte « personne présente »). Ces deux-ci sont des faits de
 * la demande et du lieu (M15).
 *
 * ⚠️ **Seul ce que l'agent a touché part** : la tablette ne connaît pas tout de la personne, et
 * renvoyer la fiche entière effacerait ce qu'elle n'affiche pas.
 */
export function ContactsCard({
  contact,
  owner,
  readOnly,
  editing,
  draft: heldDraft,
  onDraftChange,
  otherPersonPresent = false,
  onOtherPersonPresentChange,
  attendee,
  signerCapacity,
  onSignerCapacityChange,
}: ContactsCardProps) {
  const [ownDraft, setOwnDraft] = useState<ContactsDraft | null>(null)
  // ⚠️ Une correction ne survit pas au verrou : la fiche transmise pendant la saisie rouvrirait la
  // carte en édition, sans « Enregistrer » ni « Annuler ». Elle n'est lue que si le geste est offert.
  const held = heldDraft !== undefined ? heldDraft : ownDraft
  const setDraft = onDraftChange ?? setOwnDraft
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const offered = editing !== undefined && readOnly !== true
  const draft = offered ? held : null
  const initial = valuesOf(contact, owner)
  const changes = draft === null ? null : changesOf(draft.baseline, draft.values)
  const blocked = draft === null ? null : blockingOf(draft.baseline, draft.values)

  async function save(): Promise<void> {
    if (editing === undefined || changes === null) return

    setSaving(true)
    setFailure(null)
    try {
      await editing.save(changes)
      setDraft(null)
    } catch (error: unknown) {
      setFailure(error instanceof Error ? error.message : 'L’enregistrement a échoué.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader icon={<UserRound size={19} />} title="Contact">
        {offered &&
          (draft === null ? (
            <Button
              tone="neutral"
              size="sm"
              disabled={editing.unavailable !== null}
              onClick={() => {
                setFailure(null)
                setDraft({ baseline: initial, values: initial })
              }}
            >
              <Pencil size={16} aria-hidden /> Modifier
            </Button>
          ) : (
            <>
              <Button tone="ghost" size="sm" disabled={saving} onClick={() => setDraft(null)}>
                Annuler
              </Button>
              <Button
                tone="brand"
                size="sm"
                // Rien de changé, un nom effacé, ou la connexion perdue en cours de saisie : la
                // saisie reste à l'écran, seul l'envoi attend.
                disabled={
                  saving || changes === null || blocked !== null || editing.unavailable !== null
                }
                onClick={() => void save()}
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </>
          ))}
      </CardHeader>
      <CardBody>
        {offered && editing.unavailable !== null && (
          <InfoNote className={s.lead} icon={<WifiOff size={15} />}>
            {editing.unavailable}
          </InfoNote>
        )}

        {draft === null ? (
          <Reading
            contact={contact}
            otherPersonPresent={otherPersonPresent}
            signerCapacity={signerCapacity}
            onSignerCapacityChange={onSignerCapacityChange}
            readOnly={readOnly}
          />
        ) : (
          <Editing
            values={draft.values}
            onChange={(patch) => setDraft({ ...draft, values: { ...draft.values, ...patch } })}
            disabled={saving}
          />
        )}

        {draft !== null && blocked !== null && (
          <InfoNote className={s.note} icon={<TriangleAlert size={15} />} tone="warn">
            {blocked}
          </InfoNote>
        )}
        {failure !== null && (
          <p className={s.failure} role="alert">
            {failure}
          </p>
        )}
      </CardBody>

      {contact !== null && onOtherPersonPresentChange !== undefined && (
        <OtherPersonPane
          checked={otherPersonPresent}
          disabled={readOnly}
          onCheckedChange={onOtherPersonPresentChange}
        >
          {attendee}
        </OtherPersonPane>
      )}
    </Card>
  )
}

/** Le volet du signataire : sa case est son titre, ses champs s'ouvrent dessous. */
function OtherPersonPane({
  checked,
  disabled,
  onCheckedChange,
  children,
}: {
  checked: boolean
  disabled?: boolean
  onCheckedChange: (checked: boolean) => void
  children: ReactNode
}) {
  const id = useId()

  return (
    <>
      {/* Toute la bande se vise au doigt, comme la rangée à cocher (`CheckRow`). */}
      <label className={s.pane} htmlFor={id}>
        <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
        <span className={s.paneTitle}>
          <UserRound size={18} aria-hidden />
          Une autre personne est sur place, et c’est elle qui signe
        </span>
      </label>
      {checked && <CardBody>{children}</CardBody>}
    </>
  )
}

/**
 * La carte « **Propriétaire de l'habitation** » — le bloc du rapport papier, dans son ordre : nom,
 * prénom, adresse, commune, téléphone, e-mail (#350, frame Terrain 1.1b). **En lecture** : la
 * correction passe par le « Modifier » de la carte « Coordonnées », la seule écriture en ligne.
 *
 * ⚠️ `?? null` sur l'e-mail, l'adresse et la commune : un propriétaire mis en cache avant eux les
 * relit `undefined` (D22).
 */
export function OwnerCard({ owner }: { owner: Owner | null }) {
  return (
    <Card>
      <CardHeader icon={<UserRound size={19} />} title="Propriétaire de l’habitation" />
      <CardBody>
        <div className={s.grid}>
          <DataField label="Nom">
            <Value>{owner?.lastName ?? null}</Value>
          </DataField>
          <DataField label="Prénom">
            <Value>{owner?.firstName ?? null}</Value>
          </DataField>
          <DataField label="Adresse">
            <Value>{owner?.address ?? null}</Value>
          </DataField>
          <DataField label="Commune">
            <Value>{owner?.municipality ?? null}</Value>
          </DataField>
          <DataField label="Téléphone">
            <Phone phone={owner?.phone ?? null} />
          </DataField>
          <DataField label="E-mail">
            <Value mono>{owner?.email ?? null}</Value>
          </DataField>
        </div>
      </CardBody>
    </Card>
  )
}

function Reading({
  contact,
  otherPersonPresent,
  signerCapacity,
  onSignerCapacityChange,
  readOnly,
}: {
  contact: AppointmentContact | null
  otherPersonPresent: boolean
  signerCapacity?: string
  onSignerCapacityChange?: (capacity: string) => void
  readOnly?: boolean
}) {
  // Le contact signe : sa qualité se constate ici, pré-remplie de l'annoncée (M20, M32).
  const signing = contact !== null && !otherPersonPresent && onSignerCapacityChange !== undefined
  const capacity = signerCapacity ?? contact?.capacity ?? ''

  return (
    <>
      <div className={s.grid}>
        {/* Des libellés qui disent qui ils visent (consigne du développeur, 22/10/2026). */}
        <DataField
          label={
            otherPersonPresent
              ? 'Personne prévue au rendez-vous'
              : 'Personne à appeler — elle signe'
          }
        >
          <Value>{contact === null ? null : displayName(contact)}</Value>
        </DataField>
        {signing ? (
          <Field
            label="En qualité de — elle signe"
            className={capacity.trim() === '' ? claim.claimed : undefined}
          >
            {({ id }) => (
              <Input
                id={id}
                className={capacity.trim() === '' ? claim.claimedInput : undefined}
                placeholder="Choisir ou écrire"
                suggestions={CAPACITY_SUGGESTIONS}
                defaultValue={capacity}
                disabled={readOnly}
                onValueChange={onSignerCapacityChange}
              />
            )}
          </Field>
        ) : (
          <DataField label="En qualité de">
            <Value>{contact?.capacity ?? null}</Value>
          </DataField>
        )}
        <DataField label="Téléphone pour la prévenir">
          {/* Un vrai lien d'appel : l'agent est devant une porte que personne n'ouvre, et
              recopier dix chiffres au clavier pendant qu'il tient un carnet est le geste
              qu'on abandonne. C'est le manque qu'un test terrain a remonté. */}
          <Phone phone={contact?.phone ?? null} />
        </DataField>
        {/* ⚠️ `?? null` : une tablette dont le cache précède l'e-mail le relit `undefined`. */}
        <DataField label="E-mail">
          <Value mono>{contact?.email ?? null}</Value>
        </DataField>
      </div>
      {/* ⚠️ **Le contact absent se dit, il ne se laisse pas en blanc** (M21) : un agent doit
          pouvoir distinguer « personne à appeler » de « on ne vous l'a pas transmis ». */}
      {contact === null && (
        <InfoNote className={s.note} icon={<TriangleAlert size={15} />} tone="warn">
          Aucun contact enregistré sur cette demande : personne à prévenir de votre arrivée.
        </InfoNote>
      )}
    </>
  )
}

function Editing({
  values,
  onChange,
  disabled,
}: {
  values: Values
  onChange: (patch: Partial<Values>) => void
  disabled: boolean
}) {
  return (
    <>
      <SectionLabel className={s.section}>Contact</SectionLabel>
      <div className={s.fields}>
        <TextField
          label="Prénom"
          value={values.contactFirstName}
          disabled={disabled}
          onChange={(contactFirstName) => onChange({ contactFirstName })}
        />
        <TextField
          label="Nom"
          value={values.contactLastName}
          disabled={disabled}
          onChange={(contactLastName) => onChange({ contactLastName })}
        />
        <TextField
          label="Téléphone"
          inputMode="tel"
          value={values.contactPhone}
          disabled={disabled}
          warning={phoneWarning(values.contactPhone)}
          onChange={(contactPhone) => onChange({ contactPhone })}
        />
        {/* M21 sur la tablette : une adresse hors format s'enregistre telle que saisie, et le champ
            dit ce qu'elle coûte — elle ne servira à aucun envoi (la confirmation part au
            demandeur, jamais au contact). */}
        <TextField
          label="E-mail"
          inputMode="email"
          value={values.contactEmail}
          disabled={disabled}
          warning={emailWarning(values.contactEmail)}
          onChange={(contactEmail) => onChange({ contactEmail })}
        />
        <TextField
          label="En qualité de"
          suggestions={CAPACITY_SUGGESTIONS}
          value={values.contactCapacity}
          disabled={disabled}
          onChange={(contactCapacity) => onChange({ contactCapacity })}
        />
      </div>

      <SectionLabel className={s.section}>Propriétaire</SectionLabel>
      <div className={s.fields}>
        <TextField
          label="Prénom du propriétaire"
          value={values.ownerFirstName}
          disabled={disabled}
          onChange={(ownerFirstName) => onChange({ ownerFirstName })}
        />
        <TextField
          label="Nom du propriétaire"
          value={values.ownerLastName}
          disabled={disabled}
          onChange={(ownerLastName) => onChange({ ownerLastName })}
        />
        <TextField
          label="Adresse du propriétaire"
          value={values.ownerAddress}
          disabled={disabled}
          onChange={(ownerAddress) => onChange({ ownerAddress })}
        />
        <TextField
          label="Commune du propriétaire"
          value={values.ownerMunicipality}
          disabled={disabled}
          onChange={(ownerMunicipality) => onChange({ ownerMunicipality })}
        />
        <TextField
          label="Téléphone du propriétaire"
          inputMode="tel"
          value={values.ownerPhone}
          disabled={disabled}
          warning={phoneWarning(values.ownerPhone)}
          onChange={(ownerPhone) => onChange({ ownerPhone })}
        />
        <TextField
          label="E-mail du propriétaire"
          inputMode="email"
          value={values.ownerEmail}
          disabled={disabled}
          warning={emailWarning(values.ownerEmail)}
          onChange={(ownerEmail) => onChange({ ownerEmail })}
        />
      </div>
    </>
  )
}

function TextField({
  label,
  value,
  inputMode,
  suggestions,
  disabled,
  warning = null,
  onChange,
}: {
  label: string
  value: string
  inputMode?: 'tel' | 'email'
  suggestions?: readonly string[]
  disabled: boolean
  /** Ce que la saisie coûte, sans l'empêcher (M21) — ou `null`. */
  warning?: string | null
  onChange: (value: string) => void
}) {
  return (
    <Field label={label} warning={warning}>
      {({ id, hintId }) => (
        // `inputMode` et non `type` : la liste des types d'`Input` est fermée (règle 3 du design
        // system), et c'est `inputMode` qui fait monter le bon clavier sur la tablette.
        <Input
          id={id}
          aria-describedby={hintId}
          inputMode={inputMode}
          suggestions={suggestions}
          placeholder="—"
          value={value}
          disabled={disabled}
          onValueChange={onChange}
        />
      )}
    </Field>
  )
}

/**
 * Un numéro **dans la même case que ses voisins** (retour du 23/09 : le lien souligné en gros ne
 * se fondait pas dans la carte), en chasse fixe comme l'e-mail, avec « Appeler » à droite : le
 * lien d'appel reste, l'agent devant une porte que personne n'ouvre n'a pas à recopier dix
 * chiffres.
 */
function Phone({ phone }: { phone: string | null }) {
  if (phone === null) return <Value mono>{null}</Value>

  return (
    <FieldValue mono readOnly className={s.phone}>
      <span className={s.phoneNumber}>{phone}</span>
      <a className={s.call} href={telHref(phone)} aria-label={`Appeler le ${phone}`}>
        <PhoneCall size={15} aria-hidden /> Appeler
      </a>
    </FieldValue>
  )
}

/**
 * Le `tel:` d'un numéro tel que le bureau l'a saisi. Les espaces s'enlèvent, le reste ne se
 * touche pas : un `+33` ou un `.` font partie du numéro de l'usager.
 */
function telHref(phone: string): string {
  return `tel:${phone.replaceAll(' ', '')}`
}

/** Une valeur de l'onglet « Infos » : présente ou non, la boîte reste, la graisse s'allège. */
export function Value({ mono, children }: { mono?: boolean; children: string | null }) {
  return (
    <FieldValue mono={mono} empty={children === null} readOnly>
      {children ?? '—'}
    </FieldValue>
  )
}

/** La saisie, champ par champ, en texte : ce que l'écran affiche, avant tout repli. */
export interface Values {
  contactFirstName: string
  contactLastName: string
  contactPhone: string
  contactEmail: string
  contactCapacity: string
  ownerFirstName: string
  ownerLastName: string
  ownerPhone: string
  ownerEmail: string
  ownerAddress: string
  ownerMunicipality: string
}

function valuesOf(contact: AppointmentContact | null, owner: Owner | null): Values {
  return {
    contactFirstName: contact?.firstName ?? '',
    contactLastName: contact?.lastName ?? '',
    contactPhone: contact?.phone ?? '',
    contactEmail: contact?.email ?? '',
    contactCapacity: contact?.capacity ?? '',
    ownerFirstName: owner?.firstName ?? '',
    ownerLastName: owner?.lastName ?? '',
    ownerPhone: owner?.phone ?? '',
    // ⚠️ `?? ''` : un propriétaire mis en cache avant #350 relit ces trois champs `undefined` (D22).
    ownerEmail: owner?.email ?? '',
    ownerAddress: owner?.address ?? '',
    ownerMunicipality: owner?.municipality ?? '',
  }
}

/**
 * Ce que la correction demande au serveur : **les seuls champs touchés**, `null` pour un champ
 * vidé. `null` quand rien n'a changé — « Enregistrer » ne part pas pour rien.
 */
export function changesOf(before: Values, after: Values): UpdateInquiryContacts | null {
  const touched = (key: keyof Values) => after[key].trim() !== before[key].trim()
  const text = (key: keyof Values) => (after[key].trim() === '' ? null : after[key].trim())

  const contact: NonNullable<UpdateInquiryContacts['appointmentContact']> = {}
  if (touched('contactLastName')) contact.lastName = after.contactLastName.trim()
  if (touched('contactFirstName')) contact.firstName = text('contactFirstName')
  if (touched('contactPhone')) contact.phone = text('contactPhone')
  if (touched('contactEmail')) contact.email = text('contactEmail')
  if (touched('contactCapacity')) contact.capacity = text('contactCapacity')

  const owner: NonNullable<UpdateInquiryContacts['owner']> = {}
  if (touched('ownerLastName')) owner.lastName = after.ownerLastName.trim()
  if (touched('ownerFirstName')) owner.firstName = text('ownerFirstName')
  if (touched('ownerPhone')) owner.phone = text('ownerPhone')
  if (touched('ownerEmail')) owner.email = text('ownerEmail')
  if (touched('ownerAddress')) owner.address = text('ownerAddress')
  if (touched('ownerMunicipality')) owner.municipality = text('ownerMunicipality')

  const request: UpdateInquiryContacts = {}
  if (Object.keys(contact).length > 0) request.appointmentContact = contact
  if (Object.keys(owner).length > 0) request.owner = owner

  return Object.keys(request).length === 0 ? null : request
}

/**
 * Ce qui empêche l'envoi, dit en clair — **un nom connu ne s'efface pas**. Le serveur refuserait
 * (`updateInquiryContactsSchema`) ; le dire avant évite à l'agent un aller-retour pour une réponse
 * connue. ⚠️ Un nom **absent** ne bloque rien : un téléphone sans nom se garde (#268).
 */
function blockingOf(before: Values, after: Values): string | null {
  const erased = (key: keyof Values) => before[key].trim() !== '' && after[key].trim() === ''

  if (erased('contactLastName')) {
    return 'Le nom du contact ne s’efface pas : corrigez-le plutôt.'
  }
  if (erased('ownerLastName')) {
    return 'Le nom du propriétaire ne s’efface pas : corrigez-le plutôt.'
  }

  return null
}
